import { cloneElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, FocusEvent, HTMLAttributes, KeyboardEvent as EventoDeTecla, MouseEvent, ReactElement, ReactNode } from 'react';

import { Icon } from '@ds/core';
import { useCamada, useEscNaPilha } from '@ds/feedback';

/**
 * As peças de camada do Brain original — diálogo, folha, dica, painel ancorado,
 * girador e selo — com o visual dele (classes `brain-*`) e o comportamento de
 * camada do VecchIA (`useCamada`: Esc, foco que entra e volta, Tab por dentro).
 * O original usava Radix e Tailwind; aqui não há nenhum dos dois.
 */

function portal(conteudo: ReactNode): ReactNode {
    return typeof document !== 'undefined' ? createPortal(conteudo, document.body) : conteudo;
}

/* ------------------------------------------------------------------ diálogo */

export interface DialogoProps {
    aberto: boolean;
    aoFechar: () => void;
    /** Nome acessível do diálogo. */
    rotulo: string;
    tamanho?: 'md' | 'lg';
    classe?: string;
    children: ReactNode;
}

/** `Dialog` do original: centralizado, fundo escurecido, fecha no Esc e no clique fora. */
export function Dialogo({ aberto, aoFechar, rotulo, tamanho = 'lg', classe = '', children }: DialogoProps) {
    const ref = useCamada<HTMLDivElement>({ aberta: aberto, aoEsc: aoFechar });
    if (!aberto) return null;
    return portal(
        <>
            <div className="brain-dialog-overlay" role="presentation" onClick={aoFechar} />
            <div ref={ref} role="dialog" aria-modal="true" aria-label={rotulo} tabIndex={-1} className={`brain-dialog brain-dialog--${tamanho} ${classe}`}>
                {children}
            </div>
        </>,
    );
}

export function DialogoCabecalho({ children }: { children: ReactNode }) {
    return <div className="brain-dialog__header">{children}</div>;
}

export function DialogoCorpo({ children }: { children: ReactNode }) {
    return <div className="brain-dialog__body">{children}</div>;
}

export function DialogoRodape({ children }: { children: ReactNode }) {
    return <div className="brain-dialog__footer">{children}</div>;
}

/* ------------------------------------------------------------------ folha */

export interface FolhaProps {
    aberta: boolean;
    aoFechar: () => void;
    rotulo: string;
    classe?: string;
    children: ReactNode;
}

/** `Sheet side="right"` do original: prévia do arquivo e biblioteca de anexos. */
export function Folha({ aberta, aoFechar, rotulo, classe = '', children }: FolhaProps) {
    const ref = useCamada<HTMLDivElement>({ aberta, aoEsc: aoFechar });
    if (!aberta) return null;
    return portal(
        <>
            <div className="brain-sheet-overlay" role="presentation" onClick={aoFechar} />
            <div ref={ref} role="dialog" aria-modal="true" aria-label={rotulo} tabIndex={-1} className={`brain-sheet ${classe}`}>
                {children}
            </div>
        </>,
    );
}

/* ------------------------------------------------------------------ dica */

type Lado = 'top' | 'right' | 'bottom' | 'left';

interface PosicaoDaDica {
    top: number;
    left: number;
    transform: string;
}

/** Altura aproximada da dica (11 px de texto + respiro e borda): abaixo disso do topo, ela abre embaixo. */
const ALTURA_DA_DICA = 34;

function posicionar(el: HTMLElement, lado: Lado): PosicaoDaDica {
    const r = el.getBoundingClientRect();
    const folga = 6;
    switch (lado) {
        case 'right':
            return { top: r.top + r.height / 2, left: r.right + folga, transform: 'translateY(-50%)' };
        case 'left':
            return { top: r.top + r.height / 2, left: r.left - folga, transform: 'translate(-100%, -50%)' };
        case 'bottom':
            return { top: r.bottom + folga, left: r.left + r.width / 2, transform: 'translateX(-50%)' };
        default:
            /* Sem espaço acima (o botão de voz colado no alto do painel à direita): abre embaixo,
               em vez de sair cortada no topo da janela. */
            if (r.top - folga < ALTURA_DA_DICA) return { top: r.bottom + folga, left: r.left + r.width / 2, transform: 'translateX(-50%)' };
            return { top: r.top - folga, left: r.left + r.width / 2, transform: 'translate(-50%, -100%)' };
    }
}

type PropsDoGatilho = HTMLAttributes<HTMLElement>;

export interface DicaProps {
    /** Vazia, a dica não aparece (o original fazia assim no menu expandido). */
    titulo: string;
    lado?: Lado;
    children: ReactElement<PropsDoGatilho>;
}

/** Foco posto por script (o painel que abre focando um botão) não mostra a dica; foco de teclado mostra. */
function focoVisivel(el: HTMLElement): boolean {
    try {
        return el.matches(':focus-visible');
    } catch {
        return true;
    }
}

/**
 * `BrainTooltip`: rótulo visual ao passar o mouse ou focar. O nome acessível
 * continua no próprio botão (`aria-label`); a dica é só para quem enxerga.
 */
export function Dica({ titulo, lado = 'top', children }: DicaProps) {
    const [posicao, setPosicao] = useState<PosicaoDaDica | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const limpar = () => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
    };
    useEffect(() => limpar, []);

    if (!titulo) return children;

    const abrir = (el: HTMLElement) => {
        limpar();
        timer.current = setTimeout(() => setPosicao(posicionar(el, lado)), 200);
    };
    const fechar = () => {
        limpar();
        setPosicao(null);
    };
    const p = children.props;
    return (
        <>
            {cloneElement(children, {
                onMouseEnter: (e: MouseEvent<HTMLElement>) => { p.onMouseEnter?.(e); abrir(e.currentTarget); },
                onMouseLeave: (e: MouseEvent<HTMLElement>) => { p.onMouseLeave?.(e); fechar(); },
                onFocus: (e: FocusEvent<HTMLElement>) => { p.onFocus?.(e); if (focoVisivel(e.currentTarget)) abrir(e.currentTarget); },
                onBlur: (e: FocusEvent<HTMLElement>) => { p.onBlur?.(e); fechar(); },
                onClick: (e: MouseEvent<HTMLElement>) => { fechar(); p.onClick?.(e); },
            })}
            {posicao && portal(
                <div className="brain-tooltip" aria-hidden="true" style={posicao}>{titulo}</div>,
            )}
        </>
    );
}

/* ------------------------------------------------------------ painel ancorado */

export interface Retangulo {
    top: number;
    left: number;
    bottom: number;
    right: number;
    width: number;
    height: number;
}

/** `useAnchorRect` do original: a caixa do gatilho, atualizada ao rolar e redimensionar. */
export function useRetanguloDaAncora(ancora: HTMLElement | null, aberto: boolean): Retangulo | null {
    const [rect, setRect] = useState<Retangulo | null>(null);
    useLayoutEffect(() => {
        if (!aberto || !ancora) {
            setRect(null);
            return undefined;
        }
        const atualizar = () => {
            const r = ancora.getBoundingClientRect();
            setRect({ top: r.top, left: r.left, bottom: r.bottom, right: r.right, width: r.width, height: r.height });
        };
        atualizar();
        window.addEventListener('scroll', atualizar, true);
        window.addEventListener('resize', atualizar);
        return () => {
            window.removeEventListener('scroll', atualizar, true);
            window.removeEventListener('resize', atualizar);
        };
    }, [ancora, aberto]);
    return rect;
}

/** `anchorPopoverStyle` do original. */
export function estiloAncorado(
    rect: Retangulo | null,
    { minWidth = 248, offset = 8, align = 'left', placement = 'below' }: { minWidth?: number; offset?: number; align?: 'left' | 'right'; placement?: 'below' | 'above' } = {},
): CSSProperties {
    if (!rect) return { visibility: 'hidden' };
    const left = align === 'right' ? rect.right : rect.left;
    const base: CSSProperties = {
        position: 'fixed', left, minWidth, zIndex: 1299,
        transform: align === 'right' ? 'translateX(-100%)' : undefined,
    };
    if (placement === 'above') {
        return { ...base, bottom: window.innerHeight - rect.top + offset };
    }
    const top = rect.bottom + offset;
    return { ...base, top, maxHeight: Math.max(160, window.innerHeight - top - 12), overflowY: 'auto' };
}

/** `anchorSubmenuStyle` do original: submenu à direita da linha. */
export function estiloDeSubmenu(rect: Retangulo | null, { minWidth = 196, gap = 4, maxHeight = 320 } = {}): CSSProperties {
    if (!rect) return { visibility: 'hidden' };
    const disponivel = Math.max(120, window.innerHeight - 16);
    const teto = Math.min(maxHeight, disponivel);
    const top = Math.min(rect.top, window.innerHeight - teto - 8);
    /* No celular não há espaço à direita: o submenu cai por baixo da linha. */
    if (rect.right + gap + minWidth > window.innerWidth - 8) {
        return {
            position: 'fixed', left: Math.max(8, Math.min(rect.left, window.innerWidth - minWidth - 8)),
            top: Math.min(rect.bottom + gap, window.innerHeight - teto - 8), minWidth, maxHeight: teto, zIndex: 1300, overflowY: 'auto',
        };
    }
    return { position: 'fixed', left: rect.right + gap, top: Math.max(8, top), minWidth, maxHeight: teto, zIndex: 1300, overflowY: 'auto' };
}

/**
 * Fecha um painel não modal (menu, seletor) no clique fora e no Esc, e devolve o
 * foco ao gatilho no Esc — o que o Radix fazia no original.
 */
export function useFecharPorFora(aberto: boolean, dentro: (alvo: Node) => boolean, aoFechar: () => void, gatilho?: HTMLElement | null) {
    const fechar = useRef(aoFechar);
    fechar.current = aoFechar;
    const esta = useRef(dentro);
    esta.current = dentro;
    /* Na pilha do Esc: com o menu aberto dentro do painel, Esc fecha o menu, não o painel. */
    useEscNaPilha(aberto, () => {
        fechar.current();
        gatilho?.focus();
    });
    useEffect(() => {
        if (!aberto) return undefined;
        const aoApertar = (e: globalThis.MouseEvent) => {
            if (e.target instanceof Node && esta.current(e.target)) return;
            fechar.current();
        };
        document.addEventListener('mousedown', aoApertar);
        return () => document.removeEventListener('mousedown', aoApertar);
    }, [aberto]);
}

/** Setas para cima e para baixo entre os itens de um menu. */
export function useSetasNoMenu() {
    return useCallback((e: EventoDeTecla<HTMLElement>) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
        const itens = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled]), [role="menuitemradio"]:not([disabled])')];
        if (itens.length === 0) return;
        e.preventDefault();
        const atual = itens.indexOf(document.activeElement as HTMLElement);
        let proximo = 0;
        if (e.key === 'End') proximo = itens.length - 1;
        else if (e.key === 'ArrowDown') proximo = atual < 0 ? 0 : (atual + 1) % itens.length;
        else if (e.key === 'ArrowUp') proximo = atual <= 0 ? itens.length - 1 : atual - 1;
        itens[proximo]?.focus();
    }, []);
}

/* ------------------------------------------------------------- miudezas */

/** `Spinner` do original, com o glifo do Lucide. */
export function Girador({ tamanho = 16 }: { tamanho?: number }) {
    return <span className="brain-girador" style={{ display: 'inline-flex' }}><Icon name="loader-circle" size={tamanho} /></span>;
}

/** `Badge` do original. */
export function Selo({ children, tamanho = 'md' }: { children: ReactNode; tamanho?: 'sm' | 'md' }) {
    return <span className={`brain-selo${tamanho === 'sm' ? ' brain-selo--sm' : ''}`}>{children}</span>;
}
